/**
 * ui.ts — the HUD side panel for the Reflex Bandit.
 *
 * Built imperatively (no framework), mirroring mob's SidePanel pattern: the
 * constructor builds the DOM via one innerHTML template, querySelects every
 * hot element into a private field, and wires inputs via callbacks the main
 * loop registers. The main loop calls `panel.update(state)` once per tick
 * with a snapshot; the panel writes textContent / classList directly.
 *
 * Blocks, top to bottom:
 *   1. Run         — Start / Stop / Reset, speed slider, elapsed.
 *   2. This episode      — gold collected, steps, world-in-day, day shape.
 *   3. Actions           — per-arm ensemble bars (the bandit's reasoning) +
 *                         reason indicator (cold / drift / model).
 *   4. Drift             — status badge + counts (drifts, warnings) + the
 *                         forced-explore-remaining countdown.
 *   5. Memory            — day-buffer / long-buffer occupancy.
 *   6. Arm / Shape / World-per-day — selectors.
 *   7. Hyperparameters   — n_trees, min_samples_leaf, cold_start_k, drift
 *                         thresholds (lam, L_drift), refit cadence.
 *   8. Run evaluation    — launches the 3-arm × 3-seed harness; shows the
 *                         progress + results tables.
 */

import type { ArmMode, ShapeName, DriftStatus } from './constants';
import { N_ACTIONS } from './constants';

export interface PanelState {
  // ── Run ──
  running: boolean;
  elapsedMs: number;
  speedHz: number;
  // ── This episode ──
  episode: number; // world-in-day index within the current day
  worldInDay: number;
  dayIdx: number;
  dayShape: ShapeName;
  worldsPerDay: number;
  episodeGold: number; // gold collected in the current in-progress episode
  episodeGoldTotal: number; // total gold placed this episode
  episodeSteps: number;
  episodeReturn: number;
  // ── Actions (the bandit's reasoning) ──
  /** Per-action ensemble mean (length N_ACTIONS), or zeros if pre-fit. */
  armMean: Float64Array;
  /** Per-action across-tree std (length N_ACTIONS). */
  armStd: Float64Array;
  /** The action chosen on the most recent tick. */
  chosenArm: number;
  /** Why: cold / drift / model. */
  reason: 'cold' | 'drift' | 'model';
  // ── Drift ──
  driftStatus: DriftStatus;
  driftExploreRemaining: number;
  nDrift: number;
  nWarning: number;
  // ── Memory ──
  dayBufferSize: number;
  dayBufferCap: number;
  longBufferSize: number;
  longBufferCap: number;
  nObserved: number;
  // ── Forest ──
  forestFit: boolean;
  fitsRequested: number;
  fitsCompleted: number;
  lastFitMs: number;
  // ── Cumulative (across all worlds this run) ──
  totalGold: number;
  totalSteps: number;
}

export interface PanelCallbacks {
  onStart(): void;
  onStop(): void;
  onReset(): void;
  onSpeed(hz: number): void;
  onArm(mode: ArmMode): void;
  onShape(shape: ShapeName): void;
  onWorldsPerDay(n: number): void;
  onNTrees(n: number): void;
  onMinSamplesLeaf(n: number): void;
  onColdStartK(k: number): void;
  onDriftLam(lam: number): void;
  onDriftLDrift(l: number): void;
  onRefitEvery(n: number): void;
  onRevealAll(b: boolean): void;
  onShowWindow(b: boolean): void;
  onRunEvaluation(): void;
}

export const DEFAULT_TIMEOUT_MS = 10 * 60 * 1000;

export class SidePanel {
  readonly el: HTMLElement;

  constructor(cb: PanelCallbacks, state: {
    arm: ArmMode;
    shape: ShapeName;
    worldsPerDay: number;
    nTrees: number;
    minSamplesLeaf: number;
    coldStartK: number;
    driftLam: number;
    driftLDrift: number;
    refitEvery: number;
    speedHz: number;
    revealAll: boolean;
    showWindow: boolean;
  }) {
    this.el = document.createElement('aside');
    this.el.className = 'panel';
    this.el.innerHTML = `
      <div class="panel-head">
        <h1>Reflex Bandit</h1>
        <p class="tagline">Tree-ensemble contextual bandit · learns token meanings from reward</p>
      </div>

      <section class="block">
        <div class="block-title">Run</div>
        <div class="btn-row">
          <button class="btn btn-primary" id="btn-start">Start</button>
          <button class="btn btn-warn" id="btn-stop">Stop</button>
          <button class="btn" id="btn-reset">Reset</button>
        </div>
        <div class="slider-row">
          <label>Speed <span id="val-speed">${state.speedHz} Hz</span></label>
          <input type="range" id="sl-speed" min="1" max="40" step="1" value="${state.speedHz}" />
        </div>
        <div class="stat-row"><span>Elapsed</span><span id="stat-elapsed">0:00</span></div>
      </section>

      <section class="block">
        <div class="block-title">This episode</div>
        <div class="stat-row big"><span>Gold</span><span id="stat-gold">0 / 0</span></div>
        <div class="stat-row"><span>Return</span><span id="stat-return">0.0</span></div>
        <div class="stat-row"><span>Steps</span><span id="stat-steps">0</span></div>
        <div class="stat-row"><span>Day / world</span><span id="stat-day">d0 · w0/8 · circles</span></div>
      </section>

      <section class="block">
        <div class="block-title">Actions <span style="float:right;font-size:10px;color:var(--text-dim);text-transform:none" id="stat-reason">— cold —</span></div>
        <div class="arm-bars" id="arm-bars">
          ${Array.from({ length: N_ACTIONS }, (_, a) =>
            `<div class="arm-cell" data-arm="${a}"><div class="arm-bar"><div class="arm-fill" style="height:0%"></div><div class="arm-std" style="bottom:0%;height:0%"></div></div><div class="arm-name">${a}</div></div>`
          ).join('')}
        </div>
        <p class="hint">Per-action ensemble mean (blue). Chosen = green. Amber band = across-tree std (the exploration signal). When all bars are equal the model has no preference; TS samples one tree per action to break the tie.</p>
      </section>

      <section class="block">
        <div class="block-title">Drift detector</div>
        <div class="stat-row"><span>Status</span><span class="drift-badge ok" id="stat-drift">OK</span></div>
        <div class="stat-row"><span>Drifts fired</span><span id="stat-ndrift">0</span></div>
        <div class="stat-row"><span>Warnings</span><span id="stat-nwarn">0</span></div>
        <div class="stat-row"><span>Re-explore left</span><span id="stat-explore">0</span></div>
      </section>

      <section class="block">
        <div class="block-title">Memory (two-timescale)</div>
        <div class="stat-row"><span>Day buffer</span><span id="stat-daybuf">0 / 300</span></div>
        <div class="stat-row"><span>Long buffer</span><span id="stat-longbuf">0 / 800</span></div>
        <div class="stat-row"><span>Lifetime obs</span><span id="stat-nobs">0</span></div>
        <div class="stat-row"><span>Fits (req/done)</span><span id="stat-fits">0 / 0</span></div>
        <div class="stat-row"><span>Last fit</span><span id="stat-fitms">— ms</span></div>
      </section>

      <section class="block">
        <div class="block-title">Arm</div>
        <div class="seg-row" id="seg-arm">
          <button class="seg seg-arm-ts ${state.arm === 'ts' ? 'active' : ''}" data-arm="ts">TS</button>
          <button class="seg seg-arm-greedy ${state.arm === 'greedy' ? 'active' : ''}" data-arm="greedy">greedy</button>
          <button class="seg seg-arm-random ${state.arm === 'random' ? 'active' : ''}" data-arm="random">random</button>
        </div>
      </section>

      <section class="block">
        <div class="block-title">World</div>
        <label class="select-label" style="font-size:11px;color:var(--text-dim)">Shape</label>
        <div class="seg-row" id="seg-shape" style="margin-bottom:8px">
          <button class="seg ${state.shape === 'circles' ? 'active' : ''}" data-shape="circles">circles</button>
          <button class="seg ${state.shape === 'squares' ? 'active' : ''}" data-shape="squares">squares</button>
          <button class="seg ${state.shape === 'blobs' ? 'active' : ''}" data-shape="blobs">blobs</button>
          <button class="seg ${state.shape === 'spirals' ? 'active' : ''}" data-shape="spirals">spiral</button>
        </div>
        <div class="slider-row">
          <label>Worlds per day <span id="val-wpd">${state.worldsPerDay}</span></label>
          <input type="range" id="sl-wpd" min="2" max="16" step="1" value="${state.worldsPerDay}" />
        </div>
      </section>

      <section class="block">
        <div class="block-title">Hyperparameters</div>
        <div class="slider-row">
          <label>n_trees <span id="val-ntrees">${state.nTrees}</span></label>
          <input type="range" id="sl-ntrees" min="10" max="80" step="5" value="${state.nTrees}" />
        </div>
        <div class="slider-row">
          <label>min_samples_leaf <span id="val-leaf">${state.minSamplesLeaf}</span></label>
          <input type="range" id="sl-leaf" min="1" max="30" step="1" value="${state.minSamplesLeaf}" />
        </div>
        <div class="slider-row">
          <label>cold_start_k <span id="val-cold">${state.coldStartK}</span></label>
          <input type="range" id="sl-cold" min="8" max="200" step="8" value="${state.coldStartK}" />
        </div>
        <div class="slider-row">
          <label>drift λ (EWMA decay) <span id="val-lam">${state.driftLam.toFixed(2)}</span></label>
          <input type="range" id="sl-lam" min="0.01" max="0.3" step="0.01" value="${state.driftLam}" />
        </div>
        <div class="slider-row">
          <label>L_drift (z-threshold) <span id="val-ldr">${state.driftLDrift.toFixed(1)}</span></label>
          <input type="range" id="sl-ldr" min="2.0" max="5.0" step="0.1" value="${state.driftLDrift}" />
        </div>
        <div class="slider-row">
          <label>refit_every <span id="val-refit">${state.refitEvery}</span></label>
          <input type="range" id="sl-refit" min="10" max="100" step="5" value="${state.refitEvery}" />
        </div>
      </section>

      <section class="block">
        <div class="block-title">View</div>
        <label class="check-row"><input type="checkbox" id="cb-window" ${state.showWindow ? 'checked' : ''}/> Show percept window</label>
        <label class="check-row"><input type="checkbox" id="cb-reveal" ${state.revealAll ? 'checked' : ''}/> Reveal all tokens</label>
      </section>

      <section class="block">
        <div class="block-title">Run evaluation (3 arms × 3 seeds)</div>
        <p class="hint">Spawns the discrete-day (circles→squares→circles→blobs) + gradual-drift (circles→blobs blend) harness in a worker. Reproduces the post's validation numbers in-browser.</p>
        <button class="btn btn-primary" id="btn-eval" style="margin-top:6px">Run evaluation</button>
        <div id="eval-output" style="margin-top:8px;display:none">
          <div class="eval-progress" id="eval-progress">queued…</div>
          <div id="eval-results"></div>
        </div>
      </section>

      <div class="panel-foot">
        Modeled on the <a href="https://blog.vski.ai/posts/bandits-active-exploitation/" target="_blank" rel="noopener">Reflex Bandit post</a>. Source on <a href="#" id="src-link">GitHub</a>.
      </div>
    `;

    // Wire inputs.
    this.wireSlider('sl-speed', 'val-speed', (v) => `${v} Hz`, cb.onSpeed);
    this.wireSlider('sl-wpd', 'val-wpd', (v) => `${v}`, cb.onWorldsPerDay);
    this.wireSlider('sl-ntrees', 'val-ntrees', (v) => `${v}`, cb.onNTrees);
    this.wireSlider('sl-leaf', 'val-leaf', (v) => `${v}`, cb.onMinSamplesLeaf);
    this.wireSlider('sl-cold', 'val-cold', (v) => `${v}`, cb.onColdStartK);
    this.wireSlider('sl-lam', 'val-lam', (v) => v.toFixed(2), cb.onDriftLam);
    this.wireSlider('sl-ldr', 'val-ldr', (v) => v.toFixed(1), cb.onDriftLDrift);
    this.wireSlider('sl-refit', 'val-refit', (v) => `${v}`, cb.onRefitEvery);

    this.el.querySelector<HTMLButtonElement>('#btn-start')!.addEventListener('click', cb.onStart);
    this.el.querySelector<HTMLButtonElement>('#btn-stop')!.addEventListener('click', cb.onStop);
    this.el.querySelector<HTMLButtonElement>('#btn-reset')!.addEventListener('click', cb.onReset);
    this.el.querySelector<HTMLButtonElement>('#btn-eval')!.addEventListener('click', cb.onRunEvaluation);
    this.el.querySelector<HTMLInputElement>('#cb-window')!.addEventListener('change', (e) =>
      cb.onShowWindow((e.target as HTMLInputElement).checked),
    );
    this.el.querySelector<HTMLInputElement>('#cb-reveal')!.addEventListener('change', (e) =>
      cb.onRevealAll((e.target as HTMLInputElement).checked),
    );

    // Segmented selectors (mutually-exclusive active state).
    this.wireSegRow('#seg-arm', 'data-arm', (mode) => cb.onArm(mode as ArmMode));
    this.wireSegRow('#seg-shape', 'data-shape', (s) => cb.onShape(s as ShapeName));
  }

  private wireSlider(id: string, valId: string, fmt: (v: number) => string, onChange: (v: number) => void): void {
    const input = this.el.querySelector<HTMLInputElement>(`#${id}`)!;
    const val = this.el.querySelector<HTMLElement>(`#${valId}`)!;
    val.textContent = fmt(parseFloat(input.value));
    input.addEventListener('input', () => {
      const v = parseFloat(input.value);
      val.textContent = fmt(v);
      onChange(v);
    });
  }

  private wireSegRow(rowSelector: string, attr: string, onChange: (value: string) => void): void {
    const row = this.el.querySelector<HTMLElement>(rowSelector)!;
    const buttons = Array.from(row.querySelectorAll<HTMLButtonElement>(`[${attr}]`));
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        buttons.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        onChange(btn.getAttribute(attr)!);
      });
    });
  }

  /** Update the panel from a per-tick snapshot. */
  update(s: PanelState): void {
    const $ = (sel: string) => this.el.querySelector<HTMLElement>(sel)!;
    const txt = (sel: string, v: string) => { $(sel).textContent = v; };
    txt('#stat-elapsed', fmtElapsed(s.elapsedMs));
    txt('#stat-gold', `${s.episodeGold} / ${s.episodeGoldTotal}`);
    txt('#stat-return', s.episodeReturn.toFixed(1));
    txt('#stat-steps', `${s.episodeSteps}`);
    txt('#stat-day', `d${s.dayIdx} · w${s.worldInDay}/${s.worldsPerDay} · ${s.dayShape}`);
    txt('#stat-reason', s.reason === 'cold' ? '— cold start —' : s.reason === 'drift' ? '— drift re-explore —' : 'model');
    txt('#stat-ndrift', `${s.nDrift}`);
    txt('#stat-nwarn', `${s.nWarning}`);
    txt('#stat-explore', `${s.driftExploreRemaining}`);
    txt('#stat-daybuf', `${s.dayBufferSize} / ${s.dayBufferCap}`);
    txt('#stat-longbuf', `${s.longBufferSize} / ${s.longBufferCap}`);
    txt('#stat-nobs', `${s.nObserved}`);
    txt('#stat-fits', `${s.fitsRequested} / ${s.fitsCompleted}`);
    txt('#stat-fitms', s.lastFitMs > 0 ? `${s.lastFitMs.toFixed(0)} ms` : '— ms');

    // Drift badge.
    const badge = $('#stat-drift');
    badge.className = `drift-badge ${s.driftStatus}`;
    badge.textContent = s.driftStatus.toUpperCase();

    // Arm bars: normalise means + stds into [0,1] for display.
    let maxAbs = 1e-6;
    for (let a = 0; a < N_ACTIONS; a++) maxAbs = Math.max(maxAbs, Math.abs(s.armMean[a]));
    for (let a = 0; a < N_ACTIONS; a++) maxAbs = Math.max(maxAbs, s.armStd[a]);
    const cells = this.el.querySelectorAll<HTMLElement>('.arm-cell');
    cells.forEach((cell, a) => {
      const fill = cell.querySelector<HTMLElement>('.arm-fill')!;
      const stdBand = cell.querySelector<HTMLElement>('.arm-std')!;
      const meanPct = Math.max(0, Math.min(100, (s.armMean[a] / maxAbs) * 100));
      const stdPct = Math.min(100, (s.armStd[a] / maxAbs) * 100);
      fill.style.height = `${meanPct}%`;
      // Std band sits on top of the mean (offset upward by half its height).
      stdBand.style.bottom = `${meanPct}%`;
      stdBand.style.height = `${stdPct * 0.5}%`;
      cell.classList.toggle('chosen', a === s.chosenArm);
    });

    // Start / stop button disabled state.
    const startBtn = this.el.querySelector<HTMLButtonElement>('#btn-start')!;
    startBtn.disabled = s.running;
  }

  /** Show the evaluation results inline. */
  setEvalOutput(html: string, progressText: string, show: boolean): void {
    const out = this.el.querySelector<HTMLElement>('#eval-output')!;
    out.style.display = show ? 'block' : 'none';
    this.el.querySelector<HTMLElement>('#eval-progress')!.textContent = progressText;
    this.el.querySelector<HTMLElement>('#eval-results')!.innerHTML = html;
  }
}

function fmtElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${(s % 60).toString().padStart(2, '0')}`;
}
